/**
 * IntlProvider onError handler — ONE path for every environment (dev == prod).
 *
 * Why a missing key at runtime is now rare, and never a page-blanking throw:
 *
 *   - The codegen parity gate (`tools/codegen/generators/i18n-keys.mjs`)
 *     guarantees every locale carries every key + matching ICU args, and
 *     `MessageKeys<N>` makes `t('badKey')` a COMPILE error.
 *   - Dynamic keys go through `t.dyn`, whose argument is typed `DynKey<N>` =
 *     `MessageKeys<N>` — a real catalog key, nothing wider — with a hand-typed
 *     single literal rejected (`NonLiteralDynKey`) and a plain `string` rejected
 *     by the `K extends DynKey<N>` bound. So a typo'd dynamic PREFIX or FIELD is
 *     also a compile error, at the call site.
 *   - INDEXED families are bounded too. The codegen emits each array / numbered
 *     family's index list as a fixed-length tuple (`MESSAGE_INDICES`, read via
 *     `t.indices(family)`), so `t.dyn(`faqs.list.${i}.question`)` interpolates a
 *     LITERAL index union and resolves to real catalog keys.
 *
 * That last bullet used to read the other way round: the numeric index was
 * "unbounded by construction", and an out-of-range one was documented here as a
 * residual runtime miss that could not be closed at compile time. It could. It
 * was the bug — a hero component read a hardcoded 8 indices out of a 5-entry
 * catalog and shipped `home.hero.searchExamples.5` to the live homepage as
 * visible text, logging three warnings through this handler on every paint.
 *
 * The residual RUNTIME miss surface is now only a catalog edited outside the
 * gates — i.e. a CDN overlay bundle that drifted from the compiled floor.
 *
 * For that residual we WARN + capture telemetry and let react-intl render the
 * raw key — in DEV and PROD alike. Previously this handler FORKED on the
 * environment (`isProduction()`): it threw in dev/test but only warned in prod.
 * That was a real dev/prod behavioural divergence the compiler could not see,
 * and it hid in prod the exact failure it was meant to catch. It is now one
 * path.
 *
 * We NEVER throw on the render path: a throw inside React's
 * `renderToReadableStream` blanks the whole page for the user, strictly worse
 * than one mis-rendered string. The ONLY loud signal is a thrown error under the
 * Vitest runner (`isTestRunner()`) — so a genuine escape turns a unit test red —
 * gated on the TEST-RUNNER env, never on `isProduction()` / `isDev()`.
 */
import { isTestRunner } from '@letsgethome/lib/env';
import { ReactIntlErrorCode } from 'react-intl';

import { clientLogger } from '../lib/client-logger';

type IntlError = { code: string; message: string };

export function intlOnError(err: IntlError): void {
  const label =
    err.code === ReactIntlErrorCode.MISSING_TRANSLATION
      ? `MISSING TRANSLATION: ${err.message}`
      : `${err.code}: ${err.message}`;
  const message = `[i18n] ${label}`;

  // Capture — same intent on both sides of the isomorphic boundary:
  //   server  → structured stdout (CloudWatch → Loki). `console.warn` keeps the
  //             heavy pino/OTel logger out of the browser bundle and never
  //             throws, so it is safe inside `renderToReadableStream`.
  //   browser → buffered telemetry beacon → BFF → Loki (`clientLogger`).
  if (typeof window === 'undefined') {
    console.warn(message);
  } else {
    clientLogger.warn({ component: 'intl', code: err.code }, message);
  }

  // The single loud signal: fail the Vitest run so an escaped key is caught in
  // CI. This is unreachable on the production/dev SSR render path.
  if (isTestRunner()) {
    throw new Error(message);
  }
}
